/**
 * Chromium / Safari Web Speech API STT (mic → transcript).
 *
 * Used when SpeechRecognition is available so Pingflo chat does not depend on
 * loading 23MB of ONNX WASM from a CDN/GCS. Whisper remains the engine when
 * Web Speech is missing (Firefox desktop) or when a modelId is requested.
 *
 * Mobile Safari/Chrome often never fire isFinal or onend after stop(). This
 * service always settles stopListening: timeout watchdog + last-interim commit.
 */

import { hasWebSpeechRecognition, isIOSVoiceClient } from "./BrowserCapability";
import { normalizeTranscription } from "./TranscriptionNormalizer";
import { toCoachLanguage } from "./WhisperWasmService";
import { VoiceState } from "./voiceState";
import { bestSpeechText, snapshotSpeechResults } from "./speechResultMerge";

const LANG_TAGS = {
  en: "en-US",
  fr: "fr-FR",
  de: "de-DE",
  es: "es-ES",
  nl: "nl-NL",
  pt: "pt-PT",
  it: "it-IT",
};

/** iOS/Android: onend/onresult after stop() are unreliable. */
export const WEB_SPEECH_STOP_TIMEOUT_MS = 3000;
/** Trailing onresult can arrive just after onend. */
const STOP_GRACE_MS = 250;
const MAX_RESTARTS = 20;

function SpeechRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export class BrowserSpeechService {
  constructor() {
    this._rec = null;
    this._state = VoiceState.IDLE;
    this._disposed = false;
    this._transcript = "";
    this._interim = "";
    this._sessionBase = "";
    this._languageHint = "en";
    this._stopping = false;
    this._restarts = 0;
    this._stopTimer = null;
    this._graceTimer = null;

    this._onPartial = null;
    this._onFinal = null;
    this._onError = null;
    this._onProgress = null;
    this._onState = null;
    this._onLevel = null;
    this.lastMetrics = null;
  }

  onPartial(cb) {
    this._onPartial = cb;
  }
  onFinal(cb) {
    this._onFinal = cb;
  }
  onError(cb) {
    this._onError = cb;
  }
  onProgress(cb) {
    this._onProgress = cb;
  }
  onState(cb) {
    this._onState = cb;
  }
  onLevel(cb) {
    this._onLevel = cb;
  }

  getState() {
    return this._state;
  }

  _setState(state) {
    if (this._disposed) return;
    this._state = state;
    if (this._onState) this._onState(state);
  }

  _emitError(code, message, cause) {
    this._setState(code);
    if (this._onError) this._onError({ code, message, cause });
  }

  async _emitDeniedOrMissingMic(name, event) {
    let code = VoiceState.PERMISSION_DENIED;
    try {
      if (navigator.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasMic = devices.some((d) => d.kind === "audioinput");
        if (!hasMic) code = VoiceState.NO_MICROPHONE;
      }
    } catch {
      /* keep PERMISSION_DENIED */
    }
    if (this._disposed) return;
    this._emitError(code, name || "Speech recognition failed", event);
  }

  _clearStopTimers() {
    if (this._stopTimer != null) {
      clearTimeout(this._stopTimer);
      this._stopTimer = null;
    }
    if (this._graceTimer != null) {
      clearTimeout(this._graceTimer);
      this._graceTimer = null;
    }
  }

  _bestText() {
    return bestSpeechText(this._transcript, this._interim);
  }

  _commitBestText() {
    this._transcript = this._bestText();
    this._interim = "";
  }

  _previewText() {
    return this._bestText();
  }

  _freezeSession() {
    this._sessionBase = this._bestText();
    this._transcript = this._sessionBase;
    this._interim = "";
  }

  async initialize() {
    if (this._disposed) return;
    if (!hasWebSpeechRecognition()) {
      this._emitError(VoiceState.UNSUPPORTED_BROWSER, "Browser speech recognition is not available");
      throw new Error(VoiceState.UNSUPPORTED_BROWSER);
    }
  }

  /**
   * Must call rec.start() in the same turn as the tap (no await before start)
   * or iOS drops the user-gesture and recognition never captures audio.
   * @param {{ languageHint?: string }} [opts]
   */
  async startListening(opts = {}) {
    if (this._disposed) return;
    if (!hasWebSpeechRecognition()) {
      this._emitError(VoiceState.UNSUPPORTED_BROWSER, "Browser speech recognition is not available");
      return;
    }

    this._languageHint = opts.languageHint || "en";
    this._transcript = "";
    this._interim = "";
    this._sessionBase = "";
    this._stopping = false;
    this._restarts = 0;
    this._clearStopTimers();
    this._stopRec();
    this._bindAndStart({ announce: true });
  }

  _bindAndStart({ announce = false } = {}) {
    const Ctor = SpeechRecognitionCtor();
    if (!Ctor) {
      this._emitError(VoiceState.UNSUPPORTED_BROWSER, "Browser speech recognition is not available");
      return;
    }

    const rec = new Ctor();
    rec.lang = LANG_TAGS[this._languageHint] || LANG_TAGS.en;
    rec.interimResults = true;
    // iOS ignores continuous and ends after a pause; Android is fine with true.
    rec.continuous = !isIOSVoiceClient();
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      if (this._disposed) return;
      // Rebuild from the full results list: Chrome often sends cumulative
      // transcripts and resultIndex=0, so appending would stutter "hello hello".
      const { committed, interim } = snapshotSpeechResults(event.results);
      this._transcript = bestSpeechText(this._sessionBase, committed);
      this._interim = interim;
      if (this._onPartial) this._onPartial(this._previewText());
    };

    rec.onerror = (event) => {
      if (this._disposed) return;
      const name = event?.error || "";
      if (name === "no-speech" || name === "aborted") return;
      const denied = name === "not-allowed" || name === "service-not-allowed";
      this._stopping = true;
      if (denied) {
        // Web Speech often reports not-allowed when no mic is present; check devices.
        void this._emitDeniedOrMissingMic(name, event);
        return;
      }
      this._emitError(VoiceState.TRANSCRIPTION_ERROR, name || "Speech recognition failed", event);
    };

    rec.onend = () => {
      if (this._rec === rec) this._rec = null;
      this._freezeSession();
      if (this._stopping || this._disposed) return;
      if (this._state !== VoiceState.LISTENING) return;
      if (this._restarts >= MAX_RESTARTS) return;
      this._restarts += 1;
      try {
        this._bindAndStart({ announce: false });
      } catch {
        /* user can tap stop; stopListening still settles */
      }
    };

    this._rec = rec;
    try {
      rec.start();
      this._setState(VoiceState.LISTENING);
      if (announce && this._onPartial) this._onPartial("");
    } catch (err) {
      this._rec = null;
      if (announce) {
        this._emitError(VoiceState.TRANSCRIPTION_ERROR, err.message || "Failed to start speech recognition", err);
      }
      // Restart after iOS onend failed — stay LISTENING so stop can commit last interim.
    }
  }

  async stopListening() {
    if (this._disposed) return;

    this._stopping = true;
    this._setState(VoiceState.PROCESSING);

    const rec = this._rec;
    await new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        this._clearStopTimers();
        resolve();
      };
      if (!rec) {
        finish();
        return;
      }
      const finishAfterGrace = () => {
        this._graceTimer = setTimeout(finish, STOP_GRACE_MS);
      };
      rec.onend = finishAfterGrace;
      try {
        rec.stop();
      } catch {
        finish();
        return;
      }
      this._stopTimer = setTimeout(() => {
        try {
          rec.abort();
        } catch {
          /* already dead */
        }
        finish();
      }, WEB_SPEECH_STOP_TIMEOUT_MS);
    });

    this._commitBestText();
    if (rec && this._rec === rec) {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      this._rec = null;
    }

    this._emitCompleted();
  }

  _emitCompleted() {
    const raw = this._bestText();
    const text = normalizeTranscription(raw);
    this.lastMetrics = { engine: "web-speech" };
    this._setState(VoiceState.COMPLETED);
    if (this._onFinal) {
      this._onFinal({
        text,
        rawText: raw,
        language: toCoachLanguage(this._languageHint),
        metrics: this.lastMetrics,
      });
    }
    this._setState(VoiceState.IDLE);
    this._transcript = "";
    this._interim = "";
    this._sessionBase = "";
    this._stopping = false;
  }

  _stopRec() {
    const rec = this._rec;
    this._rec = null;
    if (!rec) return;
    rec.onresult = null;
    rec.onerror = null;
    rec.onend = null;
    try {
      rec.abort();
    } catch {
      /* already stopped */
    }
  }

  dispose() {
    this._disposed = true;
    this._stopping = true;
    this._clearStopTimers();
    this._stopRec();
    this._onPartial = null;
    this._onFinal = null;
    this._onError = null;
    this._onProgress = null;
    this._onState = null;
    this._onLevel = null;
    this._state = VoiceState.IDLE;
  }
}
